import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, forkJoin, of, ReplaySubject } from 'rxjs';
import { distinct, pluck, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { enumEntityModelCode } from '@data-layer/enums/entities/enumEntityCode';
import { DeliveryOrdersRepository, EntitiesRepository } from '@data-layer/repositories';
import { CompaniesService } from '@services/companies.service';
import { DeliveryOrderModel } from '@data-layer/models/entry-summaries';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { EntityModel } from '@data-layer/models/entities';
import { ShipmentModel } from '@data-layer/models/common';
import { flatten } from 'lodash-es';
import { UtilsHelper } from '@helpers/utils-helper';

@Injectable({
  providedIn: 'root',
})
export class DeliveriesOrderService implements OnDestroy {
  public currentDeliveryOrderList: BehaviorSubject<DeliveryOrderModel[]> = new BehaviorSubject([]);
  private isDestroy: ReplaySubject<boolean> = new ReplaySubject();

  constructor(
    private deliveryOrdersRepository: DeliveryOrdersRepository,
    private entrySummaryStateService: EntrySummaryStateService,
    private entitiesRepository: EntitiesRepository,
    private companiesService: CompaniesService,
  ) {}

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public clear() {
    this.currentDeliveryOrderList.complete();
    this.currentDeliveryOrderList = new BehaviorSubject([]);
  }

  public startNew() {
    this.updateDeliveriesForCurrentES();
  }

  public updateList() {
    this.updateDeliveriesForCurrentES();
  }

  public delete(id: number) {
    this.deliveryOrdersRepository
      .delete(id)
      .pipe(
        switchMap(() => this.currentDeliveryOrderList),
        take(1),
      )
      .subscribe((deliveryOrders) => {
        this.currentDeliveryOrderList.next(deliveryOrders.filter((item) => item.id !== id));
      });
  }

  public saveDeliveryOrder(shipmentId: number, data: DeliveryOrderModel) {
    const shipTo: EntityModel = data.shipTo;
    const trucker: EntityModel = data.trucker;
    delete trucker.address;
    shipTo.address.id = 0;
    return forkJoin({
      createdShipTo: this.createOrUpdateEntity(shipTo, enumEntityModelCode.SHIP_TO),
      createdTrucker: this.createOrUpdateEntity(trucker, enumEntityModelCode.TRUCKER),
    }).pipe(
      switchMap(({ createdShipTo, createdTrucker }) => this.createOrUpdateDeliveryOrder(shipmentId, createdTrucker, createdShipTo, data)),
    );
  }

  private createOrUpdateDeliveryOrder(shipmentId: number, trucker: EntityModel, shipTo: EntityModel, deliveryOrder: DeliveryOrderModel) {
    deliveryOrder.truckerId = trucker.id;
    deliveryOrder.shipToId = shipTo.id;
    if (deliveryOrder.id) {
      return this.deliveryOrdersRepository.update(deliveryOrder.id, deliveryOrder).pipe(
        tap((updated) => {
          this.updateCurrentDelivery(updated);
        }),
      );
    } else {
      return this.deliveryOrdersRepository.create(shipmentId, deliveryOrder).pipe(
        tap((created) => {
          this.addCurrentDelivery(created);
        }),
      );
    }
  }

  private updateCurrentDelivery(deliveryOrder: DeliveryOrderModel) {
    this.currentDeliveryOrderList.pipe(take(1)).subscribe((list) => {
      this.currentDeliveryOrderList.next(
        list.map((item) => {
          if (item.id === deliveryOrder.id) {
            return deliveryOrder;
          }
          return item;
        }),
      );
    });
  }

  private addCurrentDelivery(deliveryOrder: DeliveryOrderModel) {
    this.currentDeliveryOrderList.pipe(take(1)).subscribe((list) => {
      this.currentDeliveryOrderList.next(UtilsHelper.concat<DeliveryOrderModel>(list, deliveryOrder));
    });
  }

  private createOrUpdateEntity(entity: EntityModel, code: enumEntityModelCode) {
    if (!entity.entityCode) {
      entity.entityCode = code;
    }

    if (entity.id) {
      return this.entitiesRepository.updateEntity(entity.id, entity);
    } else {
      return this.entitiesRepository.createEntity(this.companiesService.getCompany().id, entity);
    }
  }

  private updateDeliveriesForCurrentES() {
    this.entrySummaryStateService.entrySummary$
      .pipe(
        pluck('shipments'),
        switchMap((shipments: ShipmentModel[]) => {
          return of((shipments || []).map((item) => item.id));
        }),
        distinct(),
        switchMap((shipmentIds: number[]) => {
          if (shipmentIds && shipmentIds.length) {
            return forkJoin(
              shipmentIds.map((shipmentId) => {
                return this.deliveryOrdersRepository.getByShipment(shipmentId);
              }),
            );
          }
          return of([]);
        }),
        takeUntil(this.isDestroy),
      )
      .subscribe((deliveryOrders: DeliveryOrderModel[][]) => {
        this.currentDeliveryOrderList.next(flatten(deliveryOrders));
      });
  }
}
